class CartDrawer extends HTMLElement {
  constructor() {
    super();

    // Add document-level ESC key listener
    this.handleEscapeKey = (evt) => {
      if (evt.code === "Escape" && this.classList.contains("active")) {
        this.close();
      }
    };
    document.addEventListener("keydown", this.handleEscapeKey);

    this.handleDocumentClick = (evt) => {
      if (!this.classList.contains("active")) return;
      const overlay = document.querySelector(".fusion-overlay-custom");
      const isClickOnOverlay = overlay && overlay.contains(evt.target);
      const isClickInsideDrawer = this.contains(evt.target);
      if (isClickOnOverlay || !isClickInsideDrawer) {
        this.close();
      }
    };
    document.addEventListener("click", this.handleDocumentClick);

    this.addEventListener("click", (evt) => {
      const overlay = evt.target.closest("#CartDrawer-Overlay");
      const closeButton = evt.target.closest(".cart-close-btn");
      if (!overlay && !closeButton) return;
      evt.preventDefault();
      this.close();
    });
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector("#cart-icon-bubble");
    if (!cartLink) return;

    cartLink.setAttribute("role", "button");
    cartLink.setAttribute("aria-haspopup", "dialog");
    cartLink.addEventListener("click", (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener("keydown", (event) => {
      if (event.code.toUpperCase() === "SPACE") {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy, options = {}) {
    if (!options.force && this.isWaitingForAddToCart()) {
      if (triggeredBy) this.pendingOpenTrigger = triggeredBy;
      return;
    }

    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute("role"))
      this.setSummaryAccessibility(cartDrawerNote);

    const revealDrawer = () => {
      // here the animation doesn't seem to always get triggered. A timeout seem to help
      setTimeout(() => {
        this.classList.add("animate", "active");
        const cartSidebar = document.querySelector(".cart-sidebar");
        if (cartSidebar) cartSidebar.classList.add("active");
        document
          .querySelector(".fusion-overlay-custom")
          ?.classList.add("overlay-active");

        if (typeof window.updateAppliedDiscountCodeUI === "function") {
          window.updateAppliedDiscountCodeUI();
        }
      });

      this.addEventListener(
        "transitionend",
        () => {
          const containerToTrapFocusOn = this.classList.contains("is-empty")
            ? this.querySelector(".drawer__inner-empty")
            : document.getElementById("CartDrawer");
          const focusElement =
            this.querySelector(".drawer__inner") ||
            this.querySelector(".drawer__close");
          trapFocus(containerToTrapFocusOn, focusElement);
        },
        { once: true },
      );

      document.body.classList.add("overflow-hidden", "z-index");
    };

    if (!options.skipRefresh && this.shouldRefreshBeforeOpen()) {
      this.classList.add("is-refreshing");
      this.refreshContentsFromCart()
        .catch((error) => {
          console.error(error);
        })
        .finally(() => {
          this.classList.remove("is-refreshing");
          revealDrawer();
        });
      return;
    }

    revealDrawer();
  }

  isWaitingForAddToCart() {
    return (
      this.classList.contains("is-add-to-cart-pending") ||
      this.classList.contains("is-rendering-cart")
    );
  }

  setPendingAddToCart(triggeredBy) {
    this.classList.add("is-add-to-cart-pending");
    if (triggeredBy) this.pendingOpenTrigger = triggeredBy;
  }

  clearPendingAddToCart() {
    this.classList.remove("is-add-to-cart-pending", "is-rendering-cart");
  }

  getKnownCartItemCount() {
    const candidates = [
      document.querySelector(".cart-btn-num"),
      document.querySelector(".cart-sidebar-title-count.cart-btn"),
      document.querySelector("[data-cart-item]"),
    ];

    for (const candidate of candidates) {
      if (!candidate) continue;
      const rawCount =
        candidate.getAttribute("data-cart-item") ||
        candidate.querySelector(".cart-count")?.textContent ||
        candidate.textContent;
      const parsedCount = parseInt(String(rawCount || "").replace(/\D/g, ""), 10);
      if (Number.isFinite(parsedCount)) return parsedCount;
    }

    return 0;
  }

  shouldRefreshBeforeOpen() {
    const knownCount = this.getKnownCartItemCount();
    const drawerLooksEmpty = !this.hasRenderedCartContents();

    return knownCount > 0 && drawerLooksEmpty;
  }

  hasRenderedCartContents() {
    const contents = this.querySelector("#CartDrawer");
    if (!contents) return false;
    if (contents.querySelector(".empty_cart_drawer")) return false;

    return Boolean(
      contents.querySelector(".cart-sidebar-product-item") &&
        contents.querySelector(".cart-footer"),
    );
  }

  refreshContentsFromCart() {
    if (this.refreshCartContentsPromise) return this.refreshCartContentsPromise;

    this.refreshCartContentsPromise = fetch(
      `${window.routes?.cart_url || "/cart"}?section_id=cart-drawer`,
      { credentials: "same-origin" },
    )
      .then((response) => response.text())
      .then((html) => {
        const parsed = new DOMParser().parseFromString(html, "text/html");
        const sourceWrapper = parsed.querySelector("#cart_drawer");
        const sourceContents = parsed.querySelector("#CartDrawer");
        const targetContents = this.querySelector("#CartDrawer");

        if (sourceWrapper) {
          this.classList.toggle(
            "is-empty",
            sourceWrapper.classList.contains("is-empty"),
          );
        }

        if (sourceContents && targetContents) {
          targetContents.innerHTML = sourceContents.innerHTML;
        }

        if (typeof window.updateProgressBar === "function") {
          window.updateProgressBar();
        }
        if (typeof window.updateAppliedDiscountCodeUI === "function") {
          window.updateAppliedDiscountCodeUI();
        }

        return this.hasRenderedCartContents();
      })
      .finally(() => {
        this.refreshCartContentsPromise = null;
      });

    return this.refreshCartContentsPromise;
  }

  close() {
    this.classList.remove("active");
    removeTrapFocus(this.activeElement);
    document.body.classList.remove("overflow-hidden");

    // Remove cart-sidebar active class
    const cartSidebar = document.querySelector(".cart-sidebar");
    if (cartSidebar) {
      cartSidebar.classList.remove("active");
    }

    // Remove overlay-active class
    const overlay = document.querySelector(".fusion-overlay-custom");
    if (overlay) {
      overlay.classList.remove("overlay-active");
    }

    // Remove z-index class from body
    document.body.classList.remove("z-index");
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute("role", "button");
    cartDrawerNote.setAttribute("aria-expanded", "false");

    if (cartDrawerNote.nextElementSibling.getAttribute("id")) {
      cartDrawerNote.setAttribute(
        "aria-controls",
        cartDrawerNote.nextElementSibling.id,
      );
    }

    cartDrawerNote.addEventListener("click", (event) => {
      event.currentTarget.setAttribute(
        "aria-expanded",
        !event.currentTarget.closest("details").hasAttribute("open"),
      );
    });

    cartDrawerNote.parentElement.addEventListener("keyup", onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.classList.add("is-rendering-cart");
    this.classList.remove("is-empty");
    this.querySelector(".drawer__inner")?.classList.remove("is-empty");
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      const sectionHtml = parsedState.sections?.[section.id];

      if (!sectionElement || !sectionHtml) return;
      const sectionInnerHTML = this.getSectionInnerHTML(
        sectionHtml,
        section.selector,
      );
      if (sectionInnerHTML == null) return;
      sectionElement.innerHTML = sectionInnerHTML;
    });

    const openWhenReady = (isReady) => {
      if (typeof window.updateAppliedDiscountCodeUI === "function") {
        window.updateAppliedDiscountCodeUI();
      }

      this.clearPendingAddToCart();
      const trigger = this.pendingOpenTrigger || this.activeElement;
      this.pendingOpenTrigger = null;

      if (!isReady) {
        console.warn(
          "Cart drawer stayed closed because fresh cart markup was not ready.",
        );
        return;
      }

      this.classList.remove("is-empty");
      this.querySelector(".drawer__inner")?.classList.remove("is-empty");
      this.open(trigger, { skipRefresh: true, force: true });
    };

    if (typeof window.updateProgressBar === "function") {
      window.updateProgressBar();
    }

    if (this.hasRenderedCartContents()) {
      openWhenReady(true);
      return Promise.resolve(true);
    }

    return this.refreshContentsFromCart()
      .then((isReady) => {
        openWhenReady(Boolean(isReady));
        return Boolean(isReady);
      })
      .catch((error) => {
        console.error(error);
        openWhenReady(false);
        return false;
      });
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector)?.innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: "cart-drawer",
        selector: "#CartDrawer",
      },
      {
        id: "cart-icon-bubble",
      },
    ];
  }

  getSectionDOM(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  disconnectedCallback() {
    // Clean up event listener when element is removed
    if (this.handleEscapeKey) {
      document.removeEventListener("keydown", this.handleEscapeKey);
    }
    if (this.handleDocumentClick) {
      document.removeEventListener("click", this.handleDocumentClick);
    }
  }
}

customElements.define("cart-drawer", CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: "CartDrawer",
        section: "cart-drawer",
        selector: ".drawer__contents",
      },
    ];
  }
}

customElements.define("cart-drawer-items", CartDrawerItems);

document.addEventListener("DOMContentLoaded", () => {
  try {
    const url = new URL(window.location.href);
    if (url.searchParams.get("open_cart_drawer") !== "1") return;

    // Clean up the URL so refresh/back doesn't keep forcing it open
    url.searchParams.delete("open_cart_drawer");
    window.history.replaceState(
      {},
      "",
      `${url.pathname}${url.search}${url.hash}`,
    );

    const drawer = document.querySelector("cart-drawer");
    if (drawer && typeof drawer.open === "function") {
      drawer.open();
    }
  } catch (e) {
    console.error(e);
  }
});

document.addEventListener("DOMContentLoaded", () => {
  if (typeof window.updateAppliedDiscountCodeUI === "function") {
    window.updateAppliedDiscountCodeUI();
  }
});
